import { Stack, Text, ActionIcon, Group, TextInput } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { IconCheck, IconPencil } from "@tabler/icons-react"
import { useState } from "react"
import { RuleGroupType } from "react-querybuilder"

import { DigestType } from "@costory/types/endpoints/digest"
import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { ContentEditor } from "@costory/front/pages/DigestV2/components/ContentEditor"
import { SuggestedAnalysisCards } from "@costory/front/pages/DigestV2/components/SuggestedAnalysisCards"
import { PageType } from "@costory/front/pages/DigestV2/components/types"
import { NodeTree } from "@costory/front/pages/DigestV2/helper"

interface DigestModalContextEditorProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>
  close: () => void
  loading: boolean
  editContent: string
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  selectedNodeDrawer: NodeTree
  digestForm: UseFormReturnType<DigestType>
  form: UseFormReturnType<Filters>
  setChartType: (type: ExplorerChartType) => void
  setPage: (page: PageType) => void
  costColumn: string
  initialFormValues: Filters
  data: {
    costCurrent: number
    costPrevious: number
  }
  exclusionWhereClause: RuleGroupType
  currentNodeFilters: Filters
  isNotOtherNode: boolean
  setLoading?: (loading: boolean) => void
}

export const DigestModalContextEditor = ({
  textareaRef,
  close,
  loading,
  editContent,
  setEditContent,
  selectedNodeDrawer,
  digestForm,
  form,
  setChartType,
  setPage,
  costColumn,
  data,
  exclusionWhereClause,
  currentNodeFilters,
  isNotOtherNode,
  setLoading,
}: DigestModalContextEditorProps) => {
  const itemIndex = digestForm.values.items.findIndex(
    (item) => item.id === selectedNodeDrawer.nodeProps.id,
  )

  const summaryValue =
    itemIndex !== -1 ? digestForm.values.items[itemIndex].summary || "" : ""
  const [textSummary, setTextSummary] = useState(summaryValue)
  const [isEditMode, toggleIsEditMode] = useDisclosure(false)
  const [suggestedSummary, setSuggestedSummary] = useState("")
  const setSummaryValueFromBilly = (summary: string) => {
    setSuggestedSummary(summary)
  }
  const rightSection =
    summaryValue.length == 0 && suggestedSummary.length > 0 ? (
      <ActionIcon
        disabled={suggestedSummary.length == 0}
        onMouseDown={() => {
          setTextSummary(suggestedSummary)
          digestForm.setFieldValue(
            `items.${itemIndex}.summary`,
            suggestedSummary,
          )
        }}
      >
        <IconCheck size={50} />
      </ActionIcon>
    ) : (
      <></>
    )
  return (
    <Stack gap="md">
      <Text fw={600} size="lg">
        Summary
      </Text>

      <Group gap="xs">
        {isEditMode ? (
          <>
            <ActionIcon
              onClick={() => {
                toggleIsEditMode.close()
                if (itemIndex !== -1) {
                  digestForm.setFieldValue(
                    `items.${itemIndex}.summary`,
                    textSummary,
                  )
                }
              }}
            >
              <IconCheck size={50} />
            </ActionIcon>
            <TextInput
              variant="unstyled"
              w="60vw"
              placeholder={suggestedSummary}
              value={textSummary}
              onChange={(e) => setTextSummary(e.target.value)}
              maxLength={150}
              onBlur={() => {
                toggleIsEditMode.close()
                if (itemIndex !== -1) {
                  digestForm.setFieldValue(
                    `items.${itemIndex}.summary`,
                    textSummary,
                  )
                }
              }}
              data-intercom-target="costory-tour-digest-modal-summary"
              rightSection={rightSection}
            />
          </>
        ) : (
          <>
            <ActionIcon onClick={toggleIsEditMode.open}>
              <IconPencil size={50} />
            </ActionIcon>
            <TextInput
              size="sm"
              w="50vw"
              onClick={toggleIsEditMode.open}
              value={
                summaryValue.length > 0
                  ? summaryValue
                  : "Click to add a summary"
              }
              variant="unstyled"
            />
          </>
        )}
      </Group>

      <SuggestedAnalysisCards
        data={data}
        initialValues={currentNodeFilters}
        digestForm={digestForm}
        form={form}
        setChartType={setChartType}
        setPage={setPage}
        costColumn={costColumn}
        textareaRef={textareaRef}
        setEditContent={setEditContent}
        editContent={editContent}
        isNotOtherNode={isNotOtherNode}
        setLoading={setLoading}
      />
      <ContentEditor
        key={selectedNodeDrawer.nodeProps.id}
        editContent={editContent}
        setEditContent={setEditContent}
        textareaRef={textareaRef}
        close={close}
        loading={loading}
        position={selectedNodeDrawer.nodeProps.position}
        digestPeriod={digestForm.values.period}
        exclusionWhereClause={exclusionWhereClause}
        costColumn={costColumn}
        setSummaryValueFromBilly={setSummaryValueFromBilly}
      />
    </Stack>
  )
}
